// Bot Manager automations config (PRD §4.4). Members read
// tenants/{t}/config/automations; owner/admin save via the automationSaveConfig
// callable. The inbound-message trigger on the backend evaluates this config and
// auto-replies through the send pipeline.
import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { useTenant } from './tenant';

export type MatchMode = 'exact' | 'contains';
export interface KeywordRule {
  id: string;
  keyword: string;
  match: MatchMode;
  reply: string;
  enabled: boolean;
}
export interface AwayConfig {
  enabled: boolean;
  message: string;
  mode: 'always' | 'schedule';
  start: string;
  end: string;
  utcOffset: number;
}
export interface AutomationsConfig {
  welcome: { enabled: boolean; message: string };
  away: AwayConfig;
  keywordRules: KeywordRule[];
}

export const DEFAULT_AUTOMATIONS: AutomationsConfig = {
  welcome: { enabled: false, message: '' },
  away: { enabled: false, message: '', mode: 'schedule', start: '09:00', end: '18:00', utcOffset: 4 },
  keywordRules: [],
};

export function useAutomations(): { config: AutomationsConfig; loading: boolean } {
  const { tenantId } = useTenant();
  const [config, setConfig] = useState<AutomationsConfig>(DEFAULT_AUTOMATIONS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setConfig(DEFAULT_AUTOMATIONS); setLoading(false); return; }
    const ref = doc(db, `tenants/${tenantId}/config/automations`);
    return onSnapshot(
      ref,
      (snap) => {
        const d = snap.data() as Partial<AutomationsConfig> | undefined;
        setConfig({
          welcome: { ...DEFAULT_AUTOMATIONS.welcome, ...(d?.welcome ?? {}) },
          away: { ...DEFAULT_AUTOMATIONS.away, ...(d?.away ?? {}) },
          keywordRules: d?.keywordRules ?? [],
        });
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [tenantId]);

  return { config, loading };
}

const saveFn = httpsCallable<AutomationsConfig, { ok: boolean }>(functions, 'automationSaveConfig');

export function saveAutomations(config: AutomationsConfig): Promise<{ ok: boolean }> {
  return saveFn(config).then((r) => r.data);
}
